// =============================================================================
// Client log forwarder
// =============================================================================
// Buffers structured client events and ships them to POST /api/client-logs,
// where the backend re-emits them through pino → Loki → Grafana. Every API call
// (method, url, status, duration, request-id) flows here from the axios
// interceptors in api.ts, so "all API calls: src, resp, status" are queryable
// in Grafana with proper filtering.
//
// Uses `fetch` directly — NOT the `api` axios instance — so forwarding a log can
// never recurse back through the logging interceptors, and the /client-logs POST
// itself is never logged.

type Level = 'debug' | 'info' | 'warn' | 'error';

export interface ClientLogEvent {
  level: Level;
  msg?: string;
  method?: string;
  url?: string;
  status?: number;
  durationMs?: number;
  requestId?: string;
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';
const ENDPOINT = `${BASE}/client-logs`;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER = 50;

let buffer: Array<ClientLogEvent & { ts: string }> = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function schedule(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush(useBeacon = false): Promise<void> {
  if (buffer.length === 0) return;
  const events = buffer;
  buffer = [];
  const payload = JSON.stringify({ events });
  try {
    // On page unload, sendBeacon is the only reliable transport.
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
  } catch {
    // Logging must never surface into app flow. Drop the batch on failure.
  }
}

/** Record a client log event. Non-blocking; batched and forwarded to the backend. */
export function logClient(ev: ClientLogEvent): void {
  buffer.push({ ...ev, ts: new Date().toISOString() });
  // Mirror to the browser console in dev for local visibility.
  if (import.meta.env.DEV) {
    const line = `[api] ${ev.method ?? ''} ${ev.url ?? ''} → ${ev.status ?? '—'} (${ev.durationMs ?? '?'}ms)`;
    if (ev.level === 'error') console.error(line, ev.msg ?? '');
    else if (ev.level === 'warn') console.warn(line, ev.msg ?? '');
    else console.log(line);
  }
  if (buffer.length >= MAX_BUFFER) void flush();
  else schedule();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => void flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}
